/**
 * FirstTimeHelpToast - One-time hint for keyboard shortcuts
 *
 * Shows a subtle toast on first use encouraging the user to
 * discover keyboard shortcuts via Shift+?
 */

import React, { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { keyboardHintsShownAtom } from "../../atoms/uiAtoms";

export const FirstTimeHelpToast: React.FC = () => {
  const [hintsShown, setHintsShown] = useAtom(keyboardHintsShownAtom);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    // Only show if hints haven't been shown before
    if (hintsShown) return;

    // Small delay before showing
    const showTimer = setTimeout(() => {
      setVisible(true);
    }, 2000);

    // Start fading after 8 seconds
    const fadeTimer = setTimeout(() => {
      setFading(true);
    }, 10000);

    // Hide and mark as shown
    const hideTimer = setTimeout(() => {
      setVisible(false);
      setHintsShown(true);
    }, 10500);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [hintsShown, setHintsShown]);

  const handleDismiss = () => {
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      setHintsShown(true);
    }, 500);
  };

  if (!visible) {
    return null;
  }

  return (
    <div
      className={`
        fixed bottom-4 left-1/2 -translate-x-1/2 z-50
        transition-all duration-500
        ${fading ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}
      `}
    >
      <div className="flex items-center gap-3 px-4 py-2 bg-bg-2/95 backdrop-blur-xs rounded-lg shadow-lg border border-border-strong">
        <span className="text-sm text-fg">
          Press{" "}
          <kbd className="px-1.5 py-0.5 bg-surface-2 rounded text-signal-text-tint font-mono text-xs">
            Shift + ?
          </kbd>{" "}
          for keyboard shortcuts
        </span>
        <button onClick={handleDismiss} className="text-fg-muted hover:text-fg text-xs">
          Got it
        </button>
      </div>
    </div>
  );
};

export default FirstTimeHelpToast;
